import cx from "classnames";
import type { HTMLAttributes, PropsWithChildren } from "react";
import { useMemo } from "react";

import type { CollectionContentTableColumnsMap } from "metabase/common/collections/columns";
import type {
  CreateBookmark,
  DeleteBookmark,
  OnCopy,
  OnMove,
  OnToggleSelectedWithItem,
} from "metabase/common/collections/types";
import { isTrashedCollection } from "metabase/common/collections/utils";
import { BaseItemsTableBody } from "metabase/common/components/ItemsTable/BaseItemsTableBody/BaseItemsTableBody";
import type { ItemRendererProps } from "metabase/common/components/ItemsTable/DefaultItemRenderer";
import { DefaultItemRenderer } from "metabase/common/components/ItemsTable/DefaultItemRenderer";
import { canSelectItems } from "metabase/common/components/ItemsTable/utils";
import { FixedSizeIcon, Flex } from "metabase/ui";
import type {
  Bookmark,
  Collection,
  CollectionItem,
  Database,
  ListCollectionItemsSortColumn,
  SortDirection,
  SortingOptions,
} from "metabase-types/api";

import { Columns } from "../Columns";
import { ColumnHeader, Table } from "../TableElements";
import type { ResponsiveProps } from "../utils";

import S from "./BaseItemsTable.module.css";

export type SortableColumnHeaderProps<SortColumn extends string> = {
  name?: SortColumn;
  sortingOptions?: SortingOptions<SortColumn>;
  onSortingOptionsChange?: (
    newSortingOptions: SortingOptions<SortColumn>,
  ) => void;
  columnHeaderProps?: Partial<HTMLAttributes<HTMLTableHeaderCellElement>>;
} & PropsWithChildren<Partial<HTMLAttributes<HTMLDivElement>>>;

export const SortableColumnHeader = <SortColumn extends string>({
  name,
  sortingOptions,
  onSortingOptionsChange,
  children,
  hideAtContainerBreakpoint,
  columnHeaderProps,
  className,
  ...props
}: SortableColumnHeaderProps<SortColumn> & ResponsiveProps) => {
  const isSortable = !!onSortingOptionsChange && !!name;
  const isSortingThisColumn = sortingOptions?.sort_column === name;
  const direction = isSortingThisColumn
    ? sortingOptions?.sort_direction
    : "desc";

  const onSortingControlClick = useMemo(() => {
    if (!isSortable) {
      return undefined;
    }
    const handler = () => {
      const nextDirection: SortDirection = direction === "asc" ? "desc" : "asc";
      const newSortingOptions = {
        sort_column: name,
        sort_direction: nextDirection,
      };
      onSortingOptionsChange?.(newSortingOptions);
    };
    return handler;
  }, [direction, isSortable, name, onSortingOptionsChange]);

  return (
    <ColumnHeader
      hideAtContainerBreakpoint={hideAtContainerBreakpoint}
      {...columnHeaderProps}
    >
      <Flex
        {...props}
        className={cx(
          S.sortingControl,
          { [S.sortable]: isSortable, [S.active]: isSortingThisColumn },
          className,
        )}
        align="center"
        onClick={onSortingControlClick}
        role="button"
      >
        {children}
        {isSortable && (
          <FixedSizeIcon
            className={S.sortingIcon}
            name={direction === "asc" ? "chevronup" : "chevrondown"}
            size={8}
            ms="xxs"
          />
        )}
      </Flex>
    </ColumnHeader>
  );
};

export type BaseItemsTableProps = {
  items: CollectionItem[];
  collection?: Collection;
  databases?: Database[];
  bookmarks?: Bookmark[];
  createBookmark?: CreateBookmark;
  deleteBookmark?: DeleteBookmark;
  selectedItems?: CollectionItem[];
  hasUnselected?: boolean;
  isPinned?: boolean;
  sortingOptions?: SortingOptions<ListCollectionItemsSortColumn>;
  onSortingOptionsChange?: (
    newSortingOptions: SortingOptions<ListCollectionItemsSortColumn>,
  ) => void;
  onToggleSelected?: OnToggleSelectedWithItem;
  onSelectAll?: () => void;
  onSelectNone?: () => void;
  onCopy?: OnCopy;
  onMove?: OnMove;
  onDrop?: () => void;
  getIsSelected?: (item: any) => boolean;
  ItemComponent?: (props: ItemRendererProps) => JSX.Element;
  onClick?: (item: CollectionItem) => void;
  visibleColumnsMap: CollectionContentTableColumnsMap;
} & Partial<Omit<HTMLAttributes<HTMLTableElement>, "onCopy" | "onClick">>;

export const BaseItemsTable = ({
  databases,
  bookmarks,
  createBookmark,
  deleteBookmark,
  items,
  collection,
  selectedItems,
  hasUnselected,
  isPinned,
  onCopy,
  onMove,
  onDrop,
  sortingOptions,
  onSortingOptionsChange,
  onToggleSelected,
  onSelectAll,
  onSelectNone,
  getIsSelected = () => false,
  ItemComponent = DefaultItemRenderer,
  visibleColumnsMap,
  onClick,
  ...props
}: BaseItemsTableProps) => {
  const canSelect = canSelectItems(collection, onToggleSelected);
  const isTrashed = !!collection && isTrashedCollection(collection);

  return (
    <Table {...props}>
      <colgroup>
        {canSelect && <Columns.Select.Col />}
        {visibleColumnsMap["type"] && <Columns.Type.Col />}
        {visibleColumnsMap["name"] && <Columns.Name.Col />}
        {visibleColumnsMap["description"] && <Columns.Description.Col />}
        {visibleColumnsMap["lastEditedBy"] && <Columns.LastEditedBy.Col />}
        {visibleColumnsMap["lastEditedAt"] && <Columns.LastEditedAt.Col />}
        {visibleColumnsMap["actionMenu"] && <Columns.ActionMenu.Col />}
        {visibleColumnsMap["archive"] && <Columns.Archive.Col />}
        <Columns.RightEdge.Col />
      </colgroup>
      <thead
        data-testid={isPinned ? "pinned-items-table-head" : "items-table-head"}
      >
        <tr>
          {canSelect && (
            <Columns.Select.Header
              selectedItems={selectedItems}
              hasUnselected={hasUnselected}
              onSelectAll={onSelectAll}
              onSelectNone={onSelectNone}
            />
          )}
          {visibleColumnsMap["type"] && (
            <Columns.Type.Header
              sortingOptions={sortingOptions}
              onSortingOptionsChange={onSortingOptionsChange}
            />
          )}
          {visibleColumnsMap["name"] && (
            <Columns.Name.Header
              sortingOptions={sortingOptions}
              onSortingOptionsChange={onSortingOptionsChange}
            />
          )}
          {visibleColumnsMap["description"] && (
            <Columns.Description.Header
              sortingOptions={sortingOptions}
              onSortingOptionsChange={onSortingOptionsChange}
            />
          )}
          {visibleColumnsMap["lastEditedBy"] && (
            <Columns.LastEditedBy.Header
              sortingOptions={sortingOptions}
              onSortingOptionsChange={onSortingOptionsChange}
              isTrashed={isTrashed}
            />
          )}
          {visibleColumnsMap["lastEditedAt"] && (
            <Columns.LastEditedAt.Header
              sortingOptions={sortingOptions}
              onSortingOptionsChange={onSortingOptionsChange}
              isTrashed={isTrashed}
            />
          )}
          {visibleColumnsMap["actionMenu"] && <Columns.ActionMenu.Header />}
          {visibleColumnsMap["archive"] && <Columns.Archive.Header />}
          <Columns.RightEdge.Header />
        </tr>
      </thead>
      <BaseItemsTableBody
        items={items}
        getIsSelected={getIsSelected}
        isPinned={isPinned}
        collection={collection}
        selectedItems={selectedItems}
        onDrop={onDrop}
        ItemComponent={ItemComponent}
        databases={databases}
        bookmarks={bookmarks}
        createBookmark={createBookmark}
        deleteBookmark={deleteBookmark}
        onCopy={onCopy}
        onMove={onMove}
        onToggleSelected={onToggleSelected}
        onClick={onClick}
        visibleColumnsMap={visibleColumnsMap}
      />
    </Table>
  );
};

BaseItemsTable.Item = DefaultItemRenderer;
